import React, { useEffect, useState } from 'react';
import AnnouncementBar from './AnnouncementBar';
import Header from './Header';
import Footer from './Footer';
import MaintenancePage from './MaintenancePage';
import { settingsService } from 'services/firestore';
import 'styles/components/layout.css';

// FEAT-03.4 STORY-03.4.02 — storefront maintenance gate.
// We read settings/store once on mount. The 60-second cache shared with
// Footer + AnnouncementBar takes care of route changes — no need to coordinate
// here. A simple re-read on focus catches operator flips that happen while
// the customer is looking at the same tab.
let _maintCache = null;
let _maintFetchedAt = 0;
const MAINT_TTL_MS = 60 * 1000;

// Test-only: clear the module-level cache so jest suites don't bleed store
// state (or the swallowed-error stale-cache value) across tests.
export const __resetMaintCacheForTests = () => {
  _maintCache = null;
  _maintFetchedAt = 0;
};

const fetchStoreSettings = async () => {
  const now = Date.now();
  if (_maintCache !== null && (now - _maintFetchedAt) < MAINT_TTL_MS) return _maintCache;
  try {
    const data = await settingsService.get('store');
    _maintCache = data || null;
    _maintFetchedAt = now;
    return _maintCache;
  } catch {
    return _maintCache;
  }
};

const Layout = ({ children }) => {
  const [store, setStore] = useState(null);
  const [storeLoaded, setStoreLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const data = await fetchStoreSettings();
      if (!cancelled) {
        setStore(data);
        setStoreLoaded(true);
      }
    };
    load();
    // Re-check on tab refocus so the operator's flip lands within a minute
    // even for a customer who left the tab open. Storefront-level caching
    // handles route navigation already.
    const onFocus = () => {
      _maintFetchedAt = 0; // bust cache on focus
      load();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Default to active — first paint must NEVER show the maintenance page
  // before we know the truth, otherwise normal navigation gets a frame of
  // "we'll be back soon" which is jarring. is_active is treated as TRUE
  // unless the doc has explicitly set it to false AFTER load.
  if (storeLoaded && store?.is_active === false) {
    return <MaintenancePage storeName={store?.name} contactEmail={store?.email} />;
  }

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <AnnouncementBar />
      <Header />
      <main id="main-content" className="main-content" tabIndex={-1}>
        {children}
      </main>
      <Footer />
    </>
  );
};

export default Layout;
